// TrichoAnalysisCard.jsx
import React, { useCallback, useMemo, useState } from "react";
import {
  faCalendarAlt,
  faClock,
  faMicroscope,
  faPen,
  faTrash,
  faUser,
} from "@fortawesome/free-solid-svg-icons";

import ActionPillButton from "../../../../../../commons/buttons/ActionPillButton";
import { Badge } from "react-bootstrap";
import ConfirmModal from "../../../../../../commons/modal/ConfirmModal";
import CustomCard from "../../../../../../commons/cards/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppToast from "../../../../../../commons/HallAppToast";
import TrichoAPI from "../../../api/TrichoAPI";
import { displayName } from "../../../../../../commons/util/HallAppUtil";

// ✅ Ajuste aqui para sua API real (pode trocar por TrichoAnalysisAPI, axios, fetch, etc.)


const trichoAPI = new TrichoAPI();

function modeLabel(mode) {
  const m = String(mode || "").toUpperCase();
  if (m === "ADV" || m === "ADVANCED") return "Avançado";
  return "Básico";
}

function modeVariant(mode) {
  const m = String(mode || "").toUpperCase();
  return m === "ADV" || m === "ADVANCED" ? "primary" : "secondary";
}

function formatDateBR(dateStr) {
  if (!dateStr) return "—";
  const [y, m, d] = String(dateStr).split("-");
  if (!y || !m || !d) return dateStr;
  return `${d}/${m}/${y}`;
}

function formatDateTimeBR(dtStr) {
  if (!dtStr) return "—";
  const dt = new Date(dtStr);
  if (Number.isNaN(dt.getTime())) return dtStr;
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yy = dt.getFullYear();
  const hh = String(dt.getHours()).padStart(2, "0");
  const mi = String(dt.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yy} ${hh}:${mi}`;
}

export default function TrichoAnalysisCard({
  item,
  onOpen,
  onEdit,
  onDelete,
}) {
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [isLoadingModalDelete, setIsLoadingModalDelete] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);

  const subtitle = useMemo(() => {
    const exam = formatDateBR(item?.examDate);
    return `Exame: ${exam}`;
  }, [item?.examDate]);

  const openDeleteModal = useCallback(
    (e) => {
      // evita abrir o card (onOpen) ao clicar em "Excluir"
      e?.stopPropagation?.();
      e?.preventDefault?.();

      setSelectedAnalysis(item);
      setShowModalDelete(true);
    },
    [item]
  );

  const closeDeleteModal = useCallback(() => {
    setShowModalDelete(false);
    setSelectedAnalysis(null);
  }, []);

  const executeDeletion = useCallback(async () => {
    if (!selectedAnalysis?.id) return;

    try {
      setIsLoadingModalDelete(true);

      // ✅ Ajuste para o método real da sua API
      // Ex.: await TrichoAnalysisAPI.delete(selectedAnalysis.id)
      await trichoAPI?.deleteAnalyseById(selectedAnalysis.id);

      closeDeleteModal();
      HallAppToast.success()

      // ✅ avisa o pai (ideal pra dar refresh/remover item)
      onDelete?.();
    } catch (err) {
      HallAppToast.genericError();
    } finally {
      setIsLoadingModalDelete(false);
    }
  }, [selectedAnalysis, onDelete, closeDeleteModal]);

  return (
    <div style={{ width: "100%" }}>
      <CustomCard>
        <div
          role="button"
          tabIndex={0}
          className="tricho-card-click"
          onClick={() => onOpen?.(item)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onOpen?.(item);
          }}
          style={{ cursor: "pointer" }}
        >
          {/* Header */}
          <div className="d-flex align-items-start justify-content-between gap-2">
            <div style={{ minWidth: 0 }}>
              <div className="fw-semibold" style={{ fontSize: 16, lineHeight: "20px" }}>
                <FontAwesomeIcon icon={faMicroscope} className="me-2" />
                Análise {item?.title}
              </div>

              <div className="text-muted" style={{ fontSize: 12 }}>
                {subtitle}
              </div>
            </div>

            <div className="d-flex flex-column align-items-end gap-2">
              <Badge bg={modeVariant(item?.analysisMode)} pill>
                {modeLabel(item?.analysisMode)}
              </Badge>
            </div>
          </div>

          <hr className="my-3" />

          {/* Badges */}
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Badge bg="light" text="dark" pill style={{ fontWeight: 900 }}>
              <FontAwesomeIcon icon={faUser} className="me-1" />
              Cliente: {displayName(item?.customerName) ?? "—"}
            </Badge>

            <Badge bg="light" text="dark" pill style={{ fontWeight: 900 }}>
              <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
              {formatDateBR(item?.examDate)}
            </Badge>

            <Badge bg="light" text="dark" pill style={{ fontWeight: 900 }}>
              <FontAwesomeIcon icon={faClock} className="me-1" />
              {formatDateTimeBR(item?.createdDate)}
            </Badge>
          </div>

          {/* Imagem principal */}
          <div className="tricho-card-thumb">
            {item?.imageUrl ? (
              <img
                src={item.imageUrl}
                alt={`Imagem análise ${item?.id}`}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  e.currentTarget.parentElement?.classList.add("is-fallback");
                }}
              />
            ) : null}

            <div className="tricho-card-thumb-fallback">
              <FontAwesomeIcon icon={faMicroscope} />
              <span>Sem imagem</span>
            </div>
          </div>

          {/* Ações */}
          <div className="d-flex gap-2 flex-wrap mt-3">
            <ActionPillButton
              icon={faPen}
              label="Editar"
              onClick={(e) => {
                e?.stopPropagation?.();
                e?.preventDefault?.();
                onEdit?.(item);
              }}
              color="#2f2a7a"
              borderColor="#dfe3ff"
              title="Editar análise"
            />

            <ActionPillButton
              icon={faTrash}
              label="Excluir"
              onClick={openDeleteModal}
              color="#b42318"
              borderColor="#ffd6d6"
              title="Excluir análise"
            />
          </div>

          <div className="text-muted mt-3" style={{ fontSize: 11 }}>
            Dica: abra para ver a imagem e as marcações.
          </div>
        </div>
      </CustomCard>

      {/* Modal de confirmação de exclusão */}
      {showModalDelete && (
        <ConfirmModal
          show={showModalDelete}
          title="Excluir"
          isLoading={isLoadingModalDelete}
          confirm={executeDeletion}
          closeModal={closeDeleteModal}
        >
          Deseja remover a análise{" "} do(a) {selectedAnalysis?.customerName}
          <span style={{ fontWeight: "bold" }}>{selectedAnalysis?.title}</span>?
        </ConfirmModal>
      )}
    </div>
  );
}